import React, { createContext, useState, useEffect } from 'react';
import decodedJWT from '../middleWare/decodedJWT';
import tokenStore from '../utils/tokenStore';
import { clearStaleSession } from '../utils/authUtils';
import {
    markSessionActive,
    shouldAttemptSessionRestore,
    shouldSkipHomepageRefresh,
} from '../utils/authRoutes';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const DataContext = createContext();

const DataProvider = ({ children }) => {
    const [data, setData] = useState(() => decodedJWT());
    const [accessToken, setAccessToken] = useState(() => tokenStore.getToken());
    const [authReady, setAuthReady] = useState(() => !!tokenStore.getToken());

    useEffect(() => {
        const unsubscribe = tokenStore.subscribe((token) => {
            setAccessToken(token);
            setData((prev) => {
                const next = decodedJWT(token);
                if (!next) return null;
                if (prev?.id === next.id) {
                    return { ...prev, ...next };
                }
                return next;
            });
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const restoreSession = async () => {
            try {
                const path = window.location?.pathname || '';
                if (!shouldAttemptSessionRestore(path)) return;
                if (shouldSkipHomepageRefresh()) return;
                if (tokenStore.getToken()) return;

                const response = await fetch(`${API_BASE}/refresh-token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.token) {
                        tokenStore.setToken(result.token);
                        markSessionActive();
                    }
                    return;
                }

                if (response.status === 400 || response.status === 401) {
                    await clearStaleSession();
                }
            } catch (_) {
                // Logged-out visitors should not see errors from silent refresh.
            } finally {
                setAuthReady(true);
            }
        };

        restoreSession();
    }, []);

    const updateAccessToken = (token) => {
        if (!token) return;
        tokenStore.setToken(token);
    };

    const clearAccessToken = () => {
        tokenStore.clearToken();
    };

    return (
        <DataContext.Provider value={{ data, setData, accessToken, setAccessToken: updateAccessToken, clearAccessToken, authReady }}>
            {children}
        </DataContext.Provider>
    );
};

export { DataContext, DataProvider };
